import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Card, Row, Col, Statistic, Spin } from 'antd';
import {
  FileTextOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ShopOutlined,
  UserOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import apiClient from '../services/api';
import { getQuoteStatusLabel } from '../utils/quoteUtils';


interface DashboardStats {
  total_quotes: number;
  total_service_providers: number;
  total_customers: number;
  total_orders: number;
  quotes_by_status: Record<string, number>;
}

const PIE_COLORS = ['#1890ff', '#fa8c16', '#52c41a', '#f5222d', '#fa541c'];

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/admin/stats');
      setStats(response.data);
    } catch (error: any) {
      setStats({
        total_quotes: 0,
        total_service_providers: 0,
        total_customers: 0,
        total_orders: 0,
        quotes_by_status: {},
      });
    } finally {
      setLoading(false);
    }
  };

  const pendingCount = (stats?.quotes_by_status?.pending_service_provider || 0) +
    (stats?.quotes_by_status?.pending_customer || 0);
  const acceptedCount = stats?.quotes_by_status?.accepted || 0;

  const pieData = stats?.quotes_by_status
    ? Object.entries(stats.quotes_by_status)
        .filter(([, count]) => count > 0)
        .map(([status, count]) => ({
          name: getQuoteStatusLabel(status, t),
          value: count,
        }))
    : [];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <h1 style={{ fontSize: 24, fontWeight: 600, color: '#334E3B', margin: 0, marginBottom: 24 }}>{t('dashboard.title')}</h1>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('dashboard.totalQuotes')}
              value={stats?.total_quotes || 0}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#334E3B' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('dashboard.pendingQuotes')}
              value={pendingCount}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('dashboard.acceptedQuotes')}
              value={acceptedCount}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('dashboard.totalSPs')}
              value={stats?.total_service_providers || 0}
              prefix={<ShopOutlined />}
              valueStyle={{ color: '#334E3B' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} sm={12} lg={12}>
          <Card>
            <Statistic
              title={t('dashboard.totalCustomers')}
              value={stats?.total_customers || 0}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#334E3B' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={12}>
          <Card hoverable onClick={() => navigate('/orders')}>
            <Statistic
              title={t('dashboard.totalOrders')}
              value={stats?.total_orders || 0}
              prefix={<ShoppingCartOutlined />}
              valueStyle={{ color: '#334E3B' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={24}>
          <Card title={t('reports.quotesByStatus')}>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {pieData.map((_, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={PIE_COLORS[index % PIE_COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                {t('dashboard.noData')}
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
